import { AddOutlined, ArrowDropDown, ContentCopy } from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  ListSubheader,
  Menu,
  MenuItem,
} from '@mui/material';
import { ReactNode, useRef, useState } from 'react';

import { TourTravel } from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { translate } from '@/ui/i18n';

import { FavoriteTravelGroupOption } from '../../utils/tour-travel-favorite-group';

export function AddTravelSplitButton({
  onClickAdd,
  favoriteTravels,
  onCopyTravel,
  favoriteGroups,
  onCopyGroup,
}: {
  onClickAdd: () => void;
  favoriteTravels: TourTravel[];
  onCopyTravel: (sourceTourTravel: TourTravel) => void;
  favoriteGroups: FavoriteTravelGroupOption[];
  onCopyGroup: (travels: TourTravel[]) => void;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const onToggleMenu = () => setMenuOpen((prev) => !prev);
  const onCloseMenu = () => setMenuOpen(false);

  const onClickCopy = (travel: TourTravel) => {
    onCloseMenu();
    onCopyTravel(travel);
  };

  const onClickCopyGroup = (group: FavoriteTravelGroupOption) => {
    onCloseMenu();
    onCopyGroup(group.travels);
  };

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        variant="outlined"
        size="small"
        sx={{ width: '100%' }}
      >
        <Button
          startIcon={<AddOutlined />}
          onClick={onClickAdd}
          sx={{ flexGrow: 1, textTransform: 'none' }}
        >
          {translate('ProjectsTour.TourTravelForm.add-travel')}
        </Button>
        <Button
          onClick={onToggleMenu}
          sx={{ flexShrink: 0, px: 0.5, minWidth: '2rem' }}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={onCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {renderMenuItems({
          favoriteTravels,
          favoriteGroups,
          onClickCopy,
          onClickCopyGroup,
        })}
      </Menu>
    </>
  );
}

function renderMenuItems({
  favoriteTravels,
  favoriteGroups,
  onClickCopy,
  onClickCopyGroup,
}: {
  favoriteTravels: TourTravel[];
  favoriteGroups: FavoriteTravelGroupOption[];
  onClickCopy: (travel: TourTravel) => void;
  onClickCopyGroup: (group: FavoriteTravelGroupOption) => void;
}): ReactNode {
  const hasNothing =
    favoriteTravels.length === 0 && favoriteGroups.length === 0;
  if (hasNothing) {
    return (
      <MenuItem disabled>
        {translate('ProjectsTour.TourTravelFavorite.no-favorites')}
      </MenuItem>
    );
  }

  const items: ReactNode[] = [];

  if (favoriteTravels.length > 0) {
    items.push(
      <ListSubheader
        key="travels-subheader"
        sx={{ lineHeight: '2rem', fontSize: '0.75rem' }}
      >
        {translate('ProjectsTour.TourTravelFavorite.copy-label')}
      </ListSubheader>,
    );
    favoriteTravels.forEach((travel) => {
      items.push(
        <MenuItem
          key={`travel-${travel.id}`}
          onClick={() => onClickCopy(travel)}
        >
          <ContentCopy sx={{ mr: 1, fontSize: '1rem' }} />
          {travel.name || `${travel.travelDistance} km`}
        </MenuItem>,
      );
    });
  }

  if (favoriteGroups.length > 0) {
    items.push(
      <ListSubheader
        key="groups-subheader"
        sx={{ lineHeight: '2rem', fontSize: '0.75rem' }}
      >
        {translate('ProjectsTour.TourTravelFavorite.copy-group-label')}
      </ListSubheader>,
    );
    favoriteGroups.forEach((group) => {
      items.push(
        <MenuItem
          key={`group-${group.key}`}
          onClick={() => onClickCopyGroup(group)}
        >
          <ContentCopy sx={{ mr: 1, fontSize: '1rem' }} />
          {group.label}
        </MenuItem>,
      );
    });
  }

  return items;
}
